import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '../components';
import { newsletterService } from '../services';
import { useUser, useAuth } from '../context';
import EmailBuilder from '../components/broadcast/EmailBuilder';

/**
 * Admin newsletter builder (email handoff §10; admin-rbac spec/sections.md §13.2).
 *
 * The builder itself is `components/broadcast/EmailBuilder` — the same component a club or
 * Pro page uses for its own emails — pointed at the platform newsletter endpoints and gated
 * by the staff verbs: SAVE DRAFT and SEND TEST TO ME need `edit`, DUPLICATE needs `create`,
 * the schedule / send block needs `send`. Lives inside the admin shell as a full-width
 * takeover (`embedded`); `onState` reports status, label and dirtiness up to the crumb bar.
 */
export default function NewsletterBuilderPage({ embedded = false, id: idProp, onState, onBack }) {
  const { id: idParam } = useParams();
  const id = idProp ?? idParam;
  const navigate = useNavigate();
  const { profile, walletBalance, can } = useUser();
  const { user, logout } = useAuth();

  const perms = useMemo(() => ({ canEdit: can('newsletter', 'edit'), canCreate: can('newsletter', 'create'), canSend: can('newsletter', 'send') }), [can]);
  // The admin service names its upload differently; the builder wants `uploadImage`.
  const client = useMemo(() => ({ ...newsletterService, uploadImage: newsletterService.uploadSectionImage }), []);

  const body = (
    <div className={embedded ? 'px-3 pb-10 pt-4 md:px-4' : 'p-6'}>
      <EmailBuilder
        id={id}
        client={client}
        perms={perms}
        mode="admin"
        onSavedNew={(newId) => navigate(`/admin/newsletter/${newId}`, { replace: !id || id === 'new' })}
        onState={onState}
        testTarget={user?.email || 'your inbox'}
        onBack={onBack || (() => navigate('/admin/newsletter'))}
        roleTag={profile?.role === 'superadmin' || user?.role === 'superadmin' ? 'SUPER ADMIN' : 'STAFF'}
      />
    </div>
  );

  if (embedded) return body;
  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Navbar user={profile} walletBalance={walletBalance} onLogout={logout} />
      {body}
    </div>
  );
}
